import { Controller, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { Student } from '../../../../api/types/student';
import { HUB_LEARNING_NEEDS } from '../../../../util/learning-needs';
import { Checkbox } from '../../../../v2/components/ui/checkbox';
import { SpecialNeedsLoader } from './SpecialNeedsLoader';

interface Props {
  student: Student;
  loading?: boolean;
  formUtils: UseFormReturn<SpecialNeedsData>;
}

const SpecialNeeds = ({ student, loading, formUtils }: Props) => {
  const { control, watch } = formUtils;
  const checkedNeeds = watch('specialLearningNeeds', []);

  if (loading) {
    return <SpecialNeedsLoader />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="text-lg text-grey-600">
        <p className="font-semibold">Diagnosed special learning needs</p>
        <p>
          Has {student.firstName} been diagnosed with any of the following special learning needs that our tutors may be
          able to support? (Optional)
        </p>
      </div>
      <div className="flex flex-col items-start gap-4">
        {HUB_LEARNING_NEEDS.map((need) => {
          return (
            <div key={need}>
              <label className="flex cursor-pointer items-center justify-start gap-4" htmlFor={need}>
                <Controller
                  name="specialLearningNeeds"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <Checkbox
                      id={need}
                      checked={checkedNeeds?.includes(need) ?? false}
                      onCheckedChange={(checked) => {
                        const current = checkedNeeds ?? [];
                        onChange(checked ? [...current, need] : current.filter((n) => n !== need));
                      }}
                    />
                  )}
                ></Controller>
                <span
                  className={`select-none text-grey-6 ${checkedNeeds?.includes(need) ? 'font-semibold' : 'font-normal'}`}
                >
                  {need}
                </span>
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const SpecialNeedsData = z.object({
  specialLearningNeeds: z.array(z.string()).nullish(),
});
export type SpecialNeedsData = z.infer<typeof SpecialNeedsData>;

export { SpecialNeeds };
